<template>
  <div class="hero-bg skinc-container">
    <div ref="storyText" class="story-text">
      <h1 class="story-title">
        <span class="line">SK ESG Story</span>
      </h1>

      <template v-if="lang !== 'ENG'">
        <h4 class="story-description d-none d-md-block">
          <span class="line">ESG Story는 새로운 시간의 프런티어로</span>
          <span class="line">SK주식회사가 도전하는 오늘, 행복한 내일에 대한 기록입니다.</span>
        </h4>
        <h4 class="story-description d-block d-md-none">
          <span class="line">ESG Story는 새로운 시간의 프런티어로</span>
          <span class="line">SK주식회사가 도전하는 오늘,</span>
          <span class="line">행복한 내일에 대한 기록입니다.</span>
        </h4>
      </template>
      <template v-else>
        <h4 class="story-description d-none d-md-block en-font">
          <span class="line"
            >The ESG Story is a record of SK Inc.’s journey into a new frontier of
          </span>
          <span class="line"
            >time-shaped by today’s challenges and the pursuit of a happier tomorrow.</span
          >
        </h4>
        <h4 class="story-description d-block d-md-none en-font">
          <span class="line">The ESG Story is a record of SK Inc.’s journey into</span>
          <span class="line">a new frontier of time-shaped by today’s challenges</span>
          <span class="line">and the pursuit of a happier tomorrow.</span>
        </h4>
      </template>
    </div>

    <div class="hero-grid">
      <div class="cards-left">
        <div v-for="(card, idx) in leftCards" :key="`left-${idx}`" class="card-container">
          <CardText
            :title="card.title"
            :description="card.desc"
            :link="card.link"
            align="left"
            :lang="lang"
          />
          <GlassyCard :image="card.img" :link="card.link" align="left" />
        </div>
      </div>

      <div class="hero-text-placeholder"></div>

      <div class="cards-right">
        <div v-for="(card, idx) in rightCards" :key="`right-${idx}`" class="card-container">
          <GlassyCard :image="card.img" :link="card.link" align="right" />
          <CardText
            :title="card.title"
            :description="card.desc"
            :link="card.link"
            align="right"
            :lang="lang"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import GlassyCard from './GlassyCard.vue';
import CardText from './CardText.vue';
import { mapState } from 'vuex';
import { heroCards_KR, heroCards_ENG } from '@/components/main_pages/skinc/skincData.js';

export default {
  name: 'HeroSection',
  components: { GlassyCard, CardText },
  data() {
    return {
      observer: null,
      visibleTimestamps: new Map()
    };
  },
  computed: {
    ...mapState('report_list', ['lang']),

    cards() {
      return this.lang === 'ENG' ? heroCards_ENG : heroCards_KR;
    },
    leftCards() {
      return this.cards.filter((_, idx) => idx % 2 === 0);
    },
    rightCards() {
      return this.cards.filter((_, idx) => idx % 2 === 1);
    }
  },
  mounted() {
    this.observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            this.visibleTimestamps.set(entry.target, Date.now());
          } else {
            const lastVisibleTime = this.visibleTimestamps.get(entry.target);
            if (lastVisibleTime && Date.now() - lastVisibleTime > 1000) {
              entry.target.classList.remove('is-visible');
            }
          }
        });
      },
      {
        rootMargin: '-20% 0px 0px 0px',
        threshold: 0.2
      }
    );

    this.observer.observe(this.$refs.storyText);
  },
  beforeDestroy() {
    if (this.observer) {
      this.observer.disconnect();
    }
  }
};
</script>

<style scoped>
.hero-bg {
  width: 100%;
  height: auto;
  min-height: 260vh;
  background: url('/static_img/skinc/esg_story/story_bg1.webp') center/cover no-repeat fixed;
  position: relative;
}

.story-text {
  position: relative;
  height: 60vh;
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  text-align: center;
  z-index: 1;
}

.story-title,
.story-description {
  overflow: hidden;
  padding: 2px 0;
}

.line {
  display: block;
  opacity: 0;
  transform: translateY(20px);
  transition: opacity 0.8s ease-out, transform 0.8s ease-out;
}

.story-text.is-visible .line {
  opacity: 1;
  transform: translateY(0);
}

@media (min-width: 768px) {
  .story-text.is-visible .story-title .line {
    transition-delay: 0.2s;
  }

  .story-text.is-visible .story-description .line:nth-child(1) {
    transition-delay: 0.4s;
  }

  .story-text.is-visible .story-description .line:nth-child(2) {
    transition-delay: 0.6s;
  }
}

@media (max-width: 767px) {
  .story-text.is-visible .story-title .line {
    transition-delay: 0.2s;
  }

  .story-text.is-visible .story-description .line:nth-child(1) {
    transition-delay: 0.4s;
  }

  .story-text.is-visible .story-description .line:nth-child(2) {
    transition-delay: 0.6s;
  }

  .story-text.is-visible .story-description .line:nth-child(3) {
    transition-delay: 0.8s;
  }
}

.card-container {
  position: relative;
  display: flex;
  align-items: center;
}

.hero-grid {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  grid-template-rows: 100vh;
}

.story-title {
  font-size: clamp(40px, 5vw, 80px);
  font-weight: 600;
  line-height: 96px;
  margin-bottom: 1.5rem;
  color: white;
  text-align: center;
}

.story-description {
  font-size: clamp(18px, 2.5vw, 36px);
  line-height: 1.6;
  font-weight: 600;
  color: white;
  text-align: center;
}

.story-description.en-font {
  font-size: clamp(14.4px, 2vw, 28.8px);
}

.cards-left,
.cards-right {
  display: flex;
  flex-direction: column;
  row-gap: 500px;
  padding: 0 20px;
}

.cards-left {
  margin-top: 0;
}

.cards-right {
  margin-top: 375px;
}

.cards-left .card-container {
  justify-content: flex-end;
}

.cards-right .card-container {
  justify-content: flex-start;
}

@media (max-width: 992px) {
  .hero-grid {
    grid-template-columns: 1fr 1fr;
  }

  .hero-text-placeholder {
    display: none;
  }
}

@media (max-width: 768px) {
  .hero-bg {
    min-height: 300vh;
    background-image: url('/static_img/skinc/esg_story/Bg1_M.webp');
    background-attachment: scroll;
    background-repeat: repeat;
    background-size: auto;
  }

  .story-text {
    height: 60vh;
    padding: 0 20px;
  }

  .hero-grid {
    grid-template-columns: 1fr;
    grid-template-rows: 80vh;
  }

  .cards-left,
  .cards-right {
    row-gap: 200px;
  }

  .cards-right {
    margin-top: clamp(0vw, -29.18vh + 504.28px, 40vw);
  }

  .cards-left .card-container,
  .cards-right .card-container {
    justify-content: center;
  }
}
</style>
